import { getContrastColor } from '@/lib/colorUtils';
import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Save, Info, Loader2 } from "lucide-react";
import { logger } from "@/lib/logger";
import { ZillowIcon } from "@/components/icons/ZillowIcon";
import { RealtorIcon } from "@/components/icons/RealtorIcon";
import { HomesIcon } from "@/components/icons/HomesIcon";
import { RedfinIcon } from "@/components/icons/RedfinIcon";
import { TruliaIcon } from "@/components/icons/TruliaIcon";
import { MLSIcon } from "@/components/icons/MLSIcon";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ViewsSavesManagerProps {
  northId: string;
  isPublished?: boolean;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  onUpdate?: () => void;
}

export const ViewsSavesManager = ({
  northId,
  isPublished = false,
  primaryColor,
  secondaryColor,
  onUpdate,
}: ViewsSavesManagerProps) => {
  const queryClient = useQueryClient();
  const [zillowUrl, setZillowUrl] = useState<string>("");
  const [realtorUrl, setRealtorUrl] = useState<string>("");
  const [isFetchingRealtor, setIsFetchingRealtor] = useState(false);
  const [totalZillowViews, setTotalZillowViews] = useState<string>("");
  const [totalZillowSaves, setTotalZillowSaves] = useState<string>("");
  const [totalRealtorViews, setTotalRealtorViews] = useState<string>("");
  const [totalRealtorSaves, setTotalRealtorSaves] = useState<string>("");
  const [totalHomesViews, setTotalHomesViews] = useState<string>("");
  const [totalHomesSaves, setTotalHomesSaves] = useState<string>("");
  const [totalRedfinViews, setTotalRedfinViews] = useState<string>("");
  const [totalRedfinSaves, setTotalRedfinSaves] = useState<string>("");
  const [totalTruliaViews, setTotalTruliaViews] = useState<string>("");
  const [totalTruliaSaves, setTotalTruliaSaves] = useState<string>("");
  const [totalMlsViews, setTotalMlsViews] = useState<string>("");
  const [totalMlsSaves, setTotalMlsSaves] = useState<string>("");

  // Fetch current values
  const { data: northData, isLoading } = useQuery({
    queryKey: ['views-saves', northId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('norths')
        .select('*')
        .eq('id', northId)
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Update form values when data loads or changes
  useEffect(() => {
    if (northData) {
      setZillowUrl(northData.zillow_url || "");
      setRealtorUrl(northData.realtor_url || "");
      setTotalZillowViews(northData.total_zillow_views?.toString() || "");
      setTotalZillowSaves(northData.total_zillow_saves?.toString() || "");
      setTotalRealtorViews(northData.total_realtor_views?.toString() || "");
      setTotalRealtorSaves(northData.total_realtor_saves?.toString() || "");
      setTotalHomesViews(northData.total_homes_views?.toString() || "");
      setTotalHomesSaves(northData.total_homes_saves?.toString() || "");
      setTotalRedfinViews(northData.total_redfin_views?.toString() || "");
      setTotalRedfinSaves(northData.total_redfin_saves?.toString() || "");
      setTotalTruliaViews(northData.total_trulia_views?.toString() || "");
      setTotalTruliaSaves(northData.total_trulia_saves?.toString() || "");
      setTotalMlsViews(northData.total_mls_views?.toString() || "");
      setTotalMlsSaves(northData.total_mls_saves?.toString() || "");
    }
  }, [northData]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const updateData: any = {
        zillow_url: zillowUrl.trim() || null,
        realtor_url: realtorUrl.trim() || null,
      };

      if (totalZillowViews.trim() !== "") {
        const value = parseInt(totalZillowViews, 10);
        if (isNaN(value) || value < 0) {
          throw new Error('Zillow Views must be a non-negative number');
        }
        updateData.total_zillow_views = value;
      } else {
        updateData.total_zillow_views = null;
      }

      if (totalZillowSaves.trim() !== "") {
        const value = parseInt(totalZillowSaves, 10);
        if (isNaN(value) || value < 0) {
          throw new Error('Zillow Saves must be a non-negative number');
        }
        updateData.total_zillow_saves = value;
      } else {
        updateData.total_zillow_saves = null;
      }

      if (totalRealtorViews.trim() !== "") {
        const value = parseInt(totalRealtorViews, 10);
        if (isNaN(value) || value < 0) {
          throw new Error('Realtor.com Views must be a non-negative number');
        }
        updateData.total_realtor_views = value;
      } else {
        updateData.total_realtor_views = null;
      }

      if (totalRealtorSaves.trim() !== "") {
        const value = parseInt(totalRealtorSaves, 10);
        if (isNaN(value) || value < 0) {
          throw new Error('Realtor.com Saves must be a non-negative number');
        }
        updateData.total_realtor_saves = value;
      } else {
        updateData.total_realtor_saves = null;
      }

      if (totalHomesViews.trim() !== "") {
        const value = parseInt(totalHomesViews, 10);
        if (isNaN(value) || value < 0) {
          throw new Error('Homes.com Views must be a non-negative number');
        }
        updateData.total_homes_views = value;
      } else {
        updateData.total_homes_views = null;
      }

      if (totalHomesSaves.trim() !== "") {
        const value = parseInt(totalHomesSaves, 10);
        if (isNaN(value) || value < 0) {
          throw new Error('Homes.com Saves must be a non-negative number');
        }
        updateData.total_homes_saves = value;
      } else {
        updateData.total_homes_saves = null;
      }

      if (totalRedfinViews.trim() !== "") {
        const value = parseInt(totalRedfinViews, 10);
        if (isNaN(value) || value < 0) {
          throw new Error('Redfin Views must be a non-negative number');
        }
        updateData.total_redfin_views = value;
      } else {
        updateData.total_redfin_views = null;
      }

      if (totalRedfinSaves.trim() !== "") {
        const value = parseInt(totalRedfinSaves, 10);
        if (isNaN(value) || value < 0) {
          throw new Error('Redfin Saves must be a non-negative number');
        }
        updateData.total_redfin_saves = value;
      } else {
        updateData.total_redfin_saves = null;
      }

      if (totalTruliaViews.trim() !== "") {
        const value = parseInt(totalTruliaViews, 10);
        if (isNaN(value) || value < 0) {
          throw new Error('Trulia Views must be a non-negative number');
        }
        updateData.total_trulia_views = value;
      } else {
        updateData.total_trulia_views = null;
      }

      if (totalTruliaSaves.trim() !== "") {
        const value = parseInt(totalTruliaSaves, 10);
        if (isNaN(value) || value < 0) {
          throw new Error('Trulia Saves must be a non-negative number');
        }
        updateData.total_trulia_saves = value;
      } else {
        updateData.total_trulia_saves = null;
      }

      if (totalMlsViews.trim() !== "") {
        const value = parseInt(totalMlsViews, 10);
        if (isNaN(value) || value < 0) {
          throw new Error('Local MLS Views must be a non-negative number');
        }
        updateData.total_mls_views = value;
      } else {
        updateData.total_mls_views = null;
      }

      if (totalMlsSaves.trim() !== "") {
        const value = parseInt(totalMlsSaves, 10);
        if (isNaN(value) || value < 0) {
          throw new Error('Local MLS Saves must be a non-negative number');
        }
        updateData.total_mls_saves = value;
      } else {
        updateData.total_mls_saves = null;
      }

      // Auto-set "as of" dates: stamp today when any value is present, clear when both are empty
      const today = new Date().toISOString().split('T')[0];
      updateData.zillow_views_as_of = (totalZillowViews.trim() !== "" || totalZillowSaves.trim() !== "") ? today : null;
      updateData.realtor_views_as_of = (totalRealtorViews.trim() !== "" || totalRealtorSaves.trim() !== "") ? today : null;
      updateData.homes_views_as_of = (totalHomesViews.trim() !== "" || totalHomesSaves.trim() !== "") ? today : null;
      updateData.redfin_views_as_of = (totalRedfinViews.trim() !== "" || totalRedfinSaves.trim() !== "") ? today : null;
      updateData.trulia_views_as_of = (totalTruliaViews.trim() !== "" || totalTruliaSaves.trim() !== "") ? today : null;
      updateData.mls_views_as_of = (totalMlsViews.trim() !== "" || totalMlsSaves.trim() !== "") ? today : null;

      const { error } = await supabase
        .from('norths')
        .update(updateData)
        .eq('id', northId);

      if (error) throw error;

      // Check if Realtor URL changed and is non-empty — trigger auto-fetch
      const oldRealtorUrl = northData?.realtor_url || "";
      const newRealtorUrl = realtorUrl.trim();
      if (newRealtorUrl && newRealtorUrl !== oldRealtorUrl) {
        return { realtorUrlChanged: true, newRealtorUrl };
      }
      return { realtorUrlChanged: false };
    },
    onSuccess: async (result) => {
      queryClient.invalidateQueries({ queryKey: ['views-saves', northId] });
      queryClient.invalidateQueries({ queryKey: ['comps', northId] });
      queryClient.invalidateQueries({ queryKey: ['north', northId] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-paginated'] });
      // Force a small delay to ensure database write is propagated
      setTimeout(() => {
        // Trigger a custom event that ListingPerformance can listen to
        window.dispatchEvent(new CustomEvent('north-metrics-updated', { detail: { northId } }));
      }, 100);
      toast.success('Views & saves updated successfully');
      if (onUpdate) {
        onUpdate();
      }

      // Auto-fetch Realtor.com metrics if URL changed
      if (result?.realtorUrlChanged && result.newRealtorUrl) {
        setIsFetchingRealtor(true);
        toast.info('Fetching Realtor.com metrics...');
        try {
          const { data: realtorData, error: realtorError } = await supabase.functions.invoke(
            "fetch-realtor-metrics",
            { body: { realtorUrl: result.newRealtorUrl } }
          );

          if (realtorError) throw realtorError;

          if (realtorData) {
            const { error: updateError } = await supabase
              .from('norths')
              .update({
                realtor_views: realtorData.views || 0,
                realtor_saves: realtorData.saves || 0,
              })
              .eq('id', northId);

            if (updateError) throw updateError;

            queryClient.invalidateQueries({ queryKey: ['views-saves', northId] });
            queryClient.invalidateQueries({ queryKey: ['north', northId] });
            toast.success(`Realtor.com metrics updated: ${realtorData.views || 0} views, ${realtorData.saves || 0} saves`);
          }
        } catch (fetchErr) {
          logger.error('Error fetching Realtor.com metrics:', fetchErr);
          toast.error('Failed to fetch Realtor.com metrics. You can enter totals manually.');
        } finally {
          setIsFetchingRealtor(false);
        }
      }
    },
    onError: (error: Error) => {
      logger.error('Error saving views/saves:', error);
      toast.error(error.message || 'Failed to update views and saves');
    },
  });

  const handleSave = () => {
    saveMutation.mutate();
  };

  const formatNumber = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString();
  };

  const formatAsOfDate = (dateStr: string | null | undefined): string | null => {
    if (!dateStr) return null;
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (isLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="space-y-6 p-6">
      {isPublished && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            This report is published. Click "Unpublish" to make changes.
          </p>
        </div>
      )}

      {/* Listing URLs Section */}
      <div className="space-y-4">
        <div>
          <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">
            Listing URLs
          </h3>
          <p className="text-sm md:text-base text-muted-foreground">
            Add your listing URLs to enable automatic metric fetching.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="zillow-url" className="flex items-center gap-2">
              <ZillowIcon className="h-4 w-4" />
              Zillow URL
            </Label>
            <Input
              id="zillow-url"
              value={zillowUrl}
              onChange={(e) => setZillowUrl(e.target.value)}
              placeholder="https://www.zillow.com/..."
              disabled={isPublished}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="realtor-url" className="flex items-center gap-2">
              <RealtorIcon className="h-4 w-4" />
              Realtor.com URL (Optional)
            </Label>
            <Input
              id="realtor-url"
              value={realtorUrl}
              onChange={(e) => setRealtorUrl(e.target.value)}
              placeholder="https://www.realtor.com/realestateandhomes-detail/..."
              disabled={isPublished}
            />
          </div>
        </div>
      </div>

      <hr className="border-border" />

      {/* Total Views & Saves Section */}
      <div className="space-y-4">
        <div>
          <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">
            Total Views & Saves
          </h3>
          <p className="text-sm md:text-base text-muted-foreground">
            Enter all-time totals for views and saves. Zillow and Realtor.com only provide data from the last 30 days.
          </p>
        </div>

        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-2">
            <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Why enter totals manually?</p>
              <p>
                Zillow and Realtor.com APIs only provide views and saves from the past 30 days.
                For Homes.com, Redfin, Trulia, and Local MLS, enter your totals directly from each platform.
                The Digital Footprint section will display these totals when available.
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Zillow Section */}
          <div className="space-y-4 border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <ZillowIcon className="h-5 w-5" />
              <h4 className="text-lg font-semibold">Zillow</h4>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="total-zillow-views">Total Zillow Views</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Current 30-day views: {formatNumber(northData?.views_count)}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  id="total-zillow-views"
                  type="number"
                  min="0"
                  value={totalZillowViews}
                  onChange={(e) => setTotalZillowViews(e.target.value)}
                  placeholder="Enter total views"
                  disabled={isPublished}
                  className="text-lg"
                />
                {northData?.views_count && (
                  <p className="text-xs text-muted-foreground">
                    Last 30 days: {formatNumber(northData.views_count)} views
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="total-zillow-saves">Total Zillow Saves</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Current 30-day saves: {formatNumber(northData?.saves_count)}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  id="total-zillow-saves"
                  type="number"
                  min="0"
                  value={totalZillowSaves}
                  onChange={(e) => setTotalZillowSaves(e.target.value)}
                  placeholder="Enter total saves"
                  disabled={isPublished}
                  className="text-lg"
                />
                {northData?.saves_count && (
                  <p className="text-xs text-muted-foreground">
                    Last 30 days: {formatNumber(northData.saves_count)} saves
                  </p>
                )}
              </div>
            </div>
            {northData?.zillow_views_as_of && (
              <p className="text-xs text-muted-foreground mt-2">Views as of {formatAsOfDate(northData.zillow_views_as_of)}</p>
            )}
          </div>

          {/* Realtor.com Section */}
          <div className="space-y-4 border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <RealtorIcon className="h-5 w-5" />
              <h4 className="text-lg font-semibold">Realtor.com</h4>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="total-realtor-views">Total Realtor.com Views</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Current 30-day views: {formatNumber(northData?.realtor_views)}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  id="total-realtor-views"
                  type="number"
                  min="0"
                  value={totalRealtorViews}
                  onChange={(e) => setTotalRealtorViews(e.target.value)}
                  placeholder="Enter total views"
                  disabled={isPublished}
                  className="text-lg"
                />
                {northData?.realtor_views && (
                  <p className="text-xs text-muted-foreground">
                    Last 30 days: {formatNumber(northData.realtor_views)} views
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="total-realtor-saves">Total Realtor.com Saves</Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Current 30-day saves: {formatNumber(northData?.realtor_saves)}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  id="total-realtor-saves"
                  type="number"
                  min="0"
                  value={totalRealtorSaves}
                  onChange={(e) => setTotalRealtorSaves(e.target.value)}
                  placeholder="Enter total saves"
                  disabled={isPublished}
                  className="text-lg"
                />
                {northData?.realtor_saves && (
                  <p className="text-xs text-muted-foreground">
                    Last 30 days: {formatNumber(northData.realtor_saves)} saves
                  </p>
                )}
              </div>
            </div>
            {northData?.realtor_views_as_of && (
              <p className="text-xs text-muted-foreground mt-2">Views as of {formatAsOfDate(northData.realtor_views_as_of)}</p>
            )}
          </div>

          {/* Homes.com Section */}
          <div className="space-y-4 border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <HomesIcon className="h-5 w-5" />
              <h4 className="text-lg font-semibold">Homes.com</h4>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="total-homes-views">Total Homes.com Views</Label>
                <Input
                  id="total-homes-views"
                  type="number"
                  min="0"
                  value={totalHomesViews}
                  onChange={(e) => setTotalHomesViews(e.target.value)}
                  placeholder="Enter total views"
                  disabled={isPublished}
                  className="text-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="total-homes-saves">Total Homes.com Saves</Label>
                <Input
                  id="total-homes-saves"
                  type="number"
                  min="0"
                  value={totalHomesSaves}
                  onChange={(e) => setTotalHomesSaves(e.target.value)}
                  placeholder="Enter total saves"
                  disabled={isPublished}
                  className="text-lg"
                />
              </div>
            </div>
            {northData?.homes_views_as_of && (
              <p className="text-xs text-muted-foreground mt-2">Views as of {formatAsOfDate(northData.homes_views_as_of)}</p>
            )}
          </div>

          {/* Redfin Section */}
          <div className="space-y-4 border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <RedfinIcon className="h-5 w-5" />
              <h4 className="text-lg font-semibold">Redfin</h4>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="total-redfin-views">Total Redfin Views</Label>
                <Input
                  id="total-redfin-views"
                  type="number"
                  min="0"
                  value={totalRedfinViews}
                  onChange={(e) => setTotalRedfinViews(e.target.value)}
                  placeholder="Enter total views"
                  disabled={isPublished}
                  className="text-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="total-redfin-saves">Total Redfin Saves</Label>
                <Input
                  id="total-redfin-saves"
                  type="number"
                  min="0"
                  value={totalRedfinSaves}
                  onChange={(e) => setTotalRedfinSaves(e.target.value)}
                  placeholder="Enter total saves"
                  disabled={isPublished}
                  className="text-lg"
                />
              </div>
            </div>
            {northData?.redfin_views_as_of && (
              <p className="text-xs text-muted-foreground mt-2">Views as of {formatAsOfDate(northData.redfin_views_as_of)}</p>
            )}
          </div>

          {/* Trulia Section */}
          <div className="space-y-4 border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <TruliaIcon className="h-5 w-5" />
              <h4 className="text-lg font-semibold">Trulia</h4>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="total-trulia-views">Total Trulia Views</Label>
                <Input
                  id="total-trulia-views"
                  type="number"
                  min="0"
                  value={totalTruliaViews}
                  onChange={(e) => setTotalTruliaViews(e.target.value)}
                  placeholder="Enter total views"
                  disabled={isPublished}
                  className="text-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="total-trulia-saves">Total Trulia Saves</Label>
                <Input
                  id="total-trulia-saves"
                  type="number"
                  min="0"
                  value={totalTruliaSaves}
                  onChange={(e) => setTotalTruliaSaves(e.target.value)}
                  placeholder="Enter total saves"
                  disabled={isPublished}
                  className="text-lg"
                />
              </div>
            </div>
            {northData?.trulia_views_as_of && (
              <p className="text-xs text-muted-foreground mt-2">Views as of {formatAsOfDate(northData.trulia_views_as_of)}</p>
            )}
          </div>

          {/* Local MLS Section */}
          <div className="space-y-4 border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <MLSIcon className="h-5 w-5" />
              <h4 className="text-lg font-semibold">Local MLS</h4>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="total-mls-views">Total Local MLS Views</Label>
                <Input
                  id="total-mls-views"
                  type="number"
                  min="0"
                  value={totalMlsViews}
                  onChange={(e) => setTotalMlsViews(e.target.value)}
                  placeholder="Enter total views"
                  disabled={isPublished}
                  className="text-lg"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="total-mls-saves">Total Local MLS Saves</Label>
                <Input
                  id="total-mls-saves"
                  type="number"
                  min="0"
                  value={totalMlsSaves}
                  onChange={(e) => setTotalMlsSaves(e.target.value)}
                  placeholder="Enter total saves"
                  disabled={isPublished}
                  className="text-lg"
                />
              </div>
            </div>
            {northData?.mls_views_as_of && (
              <p className="text-xs text-muted-foreground mt-2">Views as of {formatAsOfDate(northData.mls_views_as_of)}</p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-4">
          {isFetchingRealtor && (
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Fetching Realtor.com metrics...
            </span>
          )}
          <Button
            onClick={handleSave}
            disabled={isPublished || saveMutation.isPending || isFetchingRealtor}
            style={{ backgroundColor: secondaryColor || '#1b489b', color: getContrastColor(secondaryColor || '#1b489b') }}
          >
            <Save className="h-4 w-4 mr-2" />
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

